import { defineAsyncComponent } from 'vue';

// Components
export { default as DataTable } from './components/data-table/DataTable.vue';
export { default as DataTableGrid } from './components/data-table/DataTableGrid.vue';
export { groupRowsBy, type GroupedRows, type GroupOrder } from './components/data-table/group-rows';
export { SplitterPanel as ResizablePanel } from 'radix-vue';
export { DropdownMenuPortal } from 'reka-ui';
export { default as SidePanel } from './components/sidepanel/Sidepanel.vue';
export { default as Slideout } from './components/slideout/Slideout.vue';
export { default as SegmentedToggle } from './components/segmented-toggle/SegmentedToggle.vue';
export { default as Floating } from './components/floating/Floating.vue';
export { Button, IconButton, buttonVariants, linkClasses, type ButtonVariants } from './components/button';
export * from './components/sidebar';
export { default as Avatar } from './components/avatar/Avatar.vue';
export { default as SearchSelect } from './components/search/SearchSelect.vue';
export { matchOptionsByLabel, type SearchSelectMatch, type SearchSelectOption } from './components/search/lib';
export { default as Select } from './components/select/Select.vue';
export { selectTriggerVariants, type SelectTriggerVariants } from './components/select';
// Select sub-components — used when the high-level Select doesn't fit
// (e.g. custom trigger content). SelectRoot stays imported from reka-ui.
export { default as SelectContent } from './components/select/components/SelectContent.vue';
export { default as SelectGroup } from './components/select/components/SelectGroup.vue';
export { default as SelectItem } from './components/select/components/SelectItem.vue';
export { default as SelectItemText } from './components/select/components/SelectItemText.vue';
export { default as SelectTrigger } from './components/select/components/SelectTrigger.vue';
export { default as SelectValue } from './components/select/components/SelectValue.vue';
export { default as EditableTextWithLabel } from './components/editable/EditableTextWithLabel.vue';
export { default as SkeletonCircle } from './components/skeleton/SkeletonCircle.vue';
export { default as SkeletonSquare } from './components/skeleton/SkeletonSquare.vue';
export { default as EditableText } from './components/editable/EditableText.vue';
export { default as TextArea } from './components/textarea/TextArea.vue';
export { default as Dropdown } from './components/dropdown/Dropdown.vue';
export { default as Toggle } from './components/toggle/Toggle.vue';
export { default as Temp } from './components/temp/Temp.vue';
export { default as Input } from './components/input/Input.vue';
export { default as UnitInput } from './components/input/UnitInput.vue';
export { default as PasswordInput } from './components/password-input/PasswordInput.vue';
export { default as WioniqAnimatedBackground } from './components/wioniq-animated-background/WioniqAnimatedBackground.vue';
export { AppErrorState, type ErrorCode } from './components/error-state';
export { default as SettingsCard } from './components/settings/SettingsCard.vue';
export { default as SettingsCardFieldItem } from './components/settings/SettingsCardFieldItem.vue';
export { default as SettingsCardLinkItem } from './components/settings/SettingsCardLinkItem.vue';
export { default as MenuButton } from './components/menu-button/MenuButton.vue';
export { default as WisionLogo } from './components/wision-logo/WisionLogo.vue';
export { default as Skeleton } from './components/skeleton/Skeleton.vue';
export { default as Checkbox } from './components/checkbox/Checkbox.vue';
export { default as SearchInput } from './components/search/SearchInput.vue';
export { default as CollapsibleSearch } from './components/search/CollapsibleSearch.vue';
export { default as HighlightedText } from './components/search/HighlightedText.vue';
export { default as MatchHint } from './components/search/MatchHint.vue';
export {
  highlightSegments,
  mergeRanges,
  type HighlightSegment,
  type MatchHintData,
  type TextRange,
} from './components/search/highlight';
export { default as Error } from './components/error/Error.vue';
export { default as ChipSelect } from './components/chip-select/ChipSelect.vue';
export { default as Combobox } from './components/combobox/Combobox.vue';
export { default as MultiSelectCombobox } from './components/multi-select-combobox/MultiSelectCombobox.vue';
export type { MultiSelectComboboxOption } from './components/multi-select-combobox/lib';
export { default as OperationIcon } from './components/operation-icon/OperationIcon.vue';
export { default as ResizablePanelGroup } from './components/resizable/ResizablePanelGroup.vue';
export { default as ResizableHandle } from './components/resizable/ResizableHandle.vue';
export { default as DatePicker } from './components/date-picker/DatePicker.vue';
export { default as Sonner } from './components/sonner/Sonner.vue';
// Dialog components
export { default as Dialog } from './components/dialog/Dialog.vue';
export { default as DialogClose } from './components/dialog/DialogClose.vue';
export { default as DialogContent } from './components/dialog/DialogContent.vue';
export { default as DialogDescription } from './components/dialog/DialogDescription.vue';
export { default as DialogFooter } from './components/dialog/DialogFooter.vue';
export { default as DialogHeader } from './components/dialog/DialogHeader.vue';
export { default as DialogScrollContent } from './components/dialog/DialogScrollContent.vue';
export { default as DialogTitle } from './components/dialog/DialogTitle.vue';
export { default as DialogTrigger } from './components/dialog/DialogTrigger.vue';
export { default as Label } from './components/label/Label.vue';
export { default as DropdownMenu } from './components/dropdown-menu/DropdownMenu.vue';
export { default as DropdownMenuCheckboxItem } from './components/dropdown-menu/DropdownMenuCheckboxItem.vue';
export { default as DropdownMenuContent } from './components/dropdown-menu/DropdownMenuContent.vue';
export { default as DropdownMenuGroup } from './components/dropdown-menu/DropdownMenuGroup.vue';
export { default as DropdownMenuItem } from './components/dropdown-menu/DropdownMenuItem.vue';
export { default as DropdownMenuLabel } from './components/dropdown-menu/DropdownMenuLabel.vue';
export { default as DropdownMenuRadioGroup } from './components/dropdown-menu/DropdownMenuRadioGroup.vue';
export { default as DropdownMenuRadioItem } from './components/dropdown-menu/DropdownMenuRadioItem.vue';
export { default as DropdownMenuSeparator } from './components/dropdown-menu/DropdownMenuSeparator.vue';
export { default as DropdownMenuShortcut } from './components/dropdown-menu/DropdownMenuShortcut.vue';
export { default as DropdownMenuSub } from './components/dropdown-menu/DropdownMenuSub.vue';
export { default as DropdownMenuSubContent } from './components/dropdown-menu/DropdownMenuSubContent.vue';
export { default as DropdownMenuSubTrigger } from './components/dropdown-menu/DropdownMenuSubTrigger.vue';
export { default as DropdownMenuTrigger } from './components/dropdown-menu/DropdownMenuTrigger.vue';
export { default as Tabs } from './components/tabs/Tabs.vue';
export { default as TabsList } from './components/tabs/TabsList.vue';
export { default as TabsTrigger } from './components/tabs/TabsTrigger.vue';
export { default as TabsContent } from './components/tabs/TabsContent.vue';
export { TabNavList, TabNavItem } from './components/tab-nav';
export { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './components/breadcrumb';
export { default as ChartEmptyState } from './components/chart-empty-state/ChartEmptyState.vue';
export { default as HorizontalScroller } from './components/horizontal-scroller/HorizontalScroller.vue';
export { default as BatteryIndicator } from './components/battery-indicator/BatteryIndicator.vue';
export { default as Humidity } from './components/humidity/Humidity.vue';
export { default as PasswordRequirements } from './components/PasswordRequirements.vue';
// Table components
export { default as Table } from './components/table/Table.vue';
export { default as TableBody } from './components/table/TableBody.vue';
export { default as TableCell } from './components/table/TableCell.vue';
export { default as TableHead } from './components/table/TableHead.vue';
export { default as TableHeader } from './components/table/TableHeader.vue';
export { default as TableRow } from './components/table/TableRow.vue';
export { default as TableFooter } from './components/table/TableFooter.vue';
export { default as TableCaption } from './components/table/TableCaption.vue';
export { default as TableEmpty } from './components/table/TableEmpty.vue';
// Calendar components
export { default as Calendar } from './components/calendar/Calendar.vue';
export { default as CalendarCell } from './components/calendar/CalendarCell.vue';
export { default as CalendarCellTrigger } from './components/calendar/CalendarCellTrigger.vue';
export { default as CalendarGrid } from './components/calendar/CalendarGrid.vue';
export { default as CalendarGridBody } from './components/calendar/CalendarGridBody.vue';
export { default as CalendarGridHead } from './components/calendar/CalendarGridHead.vue';
export { default as CalendarGridRow } from './components/calendar/CalendarGridRow.vue';
export { default as CalendarHeadCell } from './components/calendar/CalendarHeadCell.vue';
export { default as CalendarHeader } from './components/calendar/CalendarHeader.vue';
export { default as CalendarHeading } from './components/calendar/CalendarHeading.vue';
export { default as CalendarNextButton } from './components/calendar/CalendarNextButton.vue';
export { default as CalendarPrevButton } from './components/calendar/CalendarPrevButton.vue';
// RangeCalendar components
export { default as RangeCalendar } from './components/range-calendar/RangeCalendar.vue';
export { default as RangeCalendarCell } from './components/range-calendar/RangeCalendarCell.vue';
export { default as RangeCalendarCellTrigger } from './components/range-calendar/RangeCalendarCellTrigger.vue';
export { default as RangeCalendarGrid } from './components/range-calendar/RangeCalendarGrid.vue';
export { default as RangeCalendarGridBody } from './components/range-calendar/RangeCalendarGridBody.vue';
export { default as RangeCalendarGridHead } from './components/range-calendar/RangeCalendarGridHead.vue';
export { default as RangeCalendarGridRow } from './components/range-calendar/RangeCalendarGridRow.vue';
export { default as RangeCalendarHeadCell } from './components/range-calendar/RangeCalendarHeadCell.vue';
export { default as RangeCalendarHeader } from './components/range-calendar/RangeCalendarHeader.vue';
export { default as RangeCalendarHeading } from './components/range-calendar/RangeCalendarHeading.vue';
export { default as RangeCalendarNextButton } from './components/range-calendar/RangeCalendarNextButton.vue';
export { default as RangeCalendarPrevButton } from './components/range-calendar/RangeCalendarPrevButton.vue';
// Command components
export { default as Command } from './components/command/Command.vue';
export { default as CommandEmpty } from './components/command/CommandEmpty.vue';
export { default as CommandGroup } from './components/command/CommandGroup.vue';
export { default as CommandInput } from './components/command/CommandInput.vue';
export { default as CommandItem } from './components/command/CommandItem.vue';
export { default as CommandList } from './components/command/CommandList.vue';
export { default as CommandSeparator } from './components/command/CommandSeparator.vue';
export { default as CommandShortcut } from './components/command/CommandShortcut.vue';
// Popover components
export { default as Popover } from './components/popover/Popover.vue';
export { default as PopoverTrigger } from './components/popover/PopoverTrigger.vue';
export { default as PopoverAnchor } from './components/popover/PopoverAnchor.vue';
export { default as PopoverContent } from './components/popover/PopoverContent.vue';
// Pin Input components
export { default as PinInput } from './components/pin-input/PinInput.vue';
export { default as PinInputGroup } from './components/pin-input/PinInputGroup.vue';
export { default as PinInputInput } from './components/pin-input/PinInputInput.vue';
export { default as PinInputSeparator } from './components/pin-input/PinInputSeparator.vue';
// Carousel components
export { default as Carousel } from './components/carousel/Carousel.vue';
export { default as CarouselContent } from './components/carousel/CarouselContent.vue';
export { default as CarouselItem } from './components/carousel/CarouselItem.vue';
export { default as CarouselNext } from './components/carousel/CarouselNext.vue';
export { default as CarouselPrevious } from './components/carousel/CarouselPrevious.vue';
// Sheet components
export { default as Sheet } from './components/sheet/Sheet.vue';
export { default as SheetClose } from './components/sheet/SheetClose.vue';
export { default as SheetContent } from './components/sheet/SheetContent.vue';
export { default as SheetDescription } from './components/sheet/SheetDescription.vue';
export { default as SheetFooter } from './components/sheet/SheetFooter.vue';
export { default as SheetHeader } from './components/sheet/SheetHeader.vue';
export { default as SheetTitle } from './components/sheet/SheetTitle.vue';
export { default as SheetTrigger } from './components/sheet/SheetTrigger.vue';
// AlertDialog components
export { default as AlertDialog } from './components/alert-dialog/AlertDialog.vue';
export { default as AlertDialogAction } from './components/alert-dialog/AlertDialogAction.vue';
export { default as AlertDialogCancel } from './components/alert-dialog/AlertDialogCancel.vue';
export { default as AlertDialogContent } from './components/alert-dialog/AlertDialogContent.vue';
export { default as AlertDialogDescription } from './components/alert-dialog/AlertDialogDescription.vue';
export { default as AlertDialogFooter } from './components/alert-dialog/AlertDialogFooter.vue';
export { default as AlertDialogHeader } from './components/alert-dialog/AlertDialogHeader.vue';
export { default as AlertDialogTitle } from './components/alert-dialog/AlertDialogTitle.vue';
export { default as AlertDialogTrigger } from './components/alert-dialog/AlertDialogTrigger.vue';

// Types
export type * from './types';
export type { MetaProps } from './templates/DeviceMeta/lib';
// Export enum values (runtime values)
export { Direction } from './types';

// Utils
export * as utils from './utils/index';
export { cn } from './utils/index';
export { useCarousel, useProvideCarousel } from './utils/index';
export { provideSettingsState, useSettingsState } from './utils/index';
export { checkboxVariants, indicatorVariants } from './utils/index';
export { useBreakpoints } from './utils/useBreakpoints';
export { getInitials } from './utils/index';

// Icons
export { default as MobileIcon } from './icons/MobileIcon.vue';
export { default as MonitorIcon } from './icons/MonitorIcon.vue';
export { default as ClockIcon } from './icons/ClockIcon.vue';
export { default as LoadingSpinner } from './icons/LoadingSpinner.vue';
export { default as PlusIconLarge } from './icons/PlusIconLarge.vue';
export { default as PlusIcon } from './icons/PlusIcon.vue';
export { default as TrashCanIcon } from './icons/TrashCanIcon.vue';
export { default as RemoveIcon } from './icons/RemoveIcon.vue';
export { default as PhoneIcon } from './icons/PhoneIcon.vue';
export { default as LinkGroupIcon } from './icons/LinkGroupIcon.vue';
export { default as FullscreenIcon } from './icons/FullscreenIcon.vue';
export { default as DragHandleIcon } from './icons/DragHandleIcon.vue';
export { default as CloseIcon } from './icons/CloseIcon.vue';
export { default as ArrowIcon } from './icons/ArrowIcon.vue';
export { default as WidgetIcon } from './icons/WidgetIcon.vue';
export { default as MoreIcon } from './icons/MoreIcon.vue';
export { default as PenIcon } from './icons/PenIcon.vue';
export { default as SearchIcon } from './icons/SearchIcon.vue';
export { default as ResetIcon } from './icons/ResetIcon.vue';
export { default as DotLoader } from './icons/DotLoader.vue';
export { default as SettingsIcon } from './icons/SettingsIcon.vue';
export { default as PlaceIcon } from './icons/PlaceIcon.vue';
export { default as DeviceIcon } from './icons/DeviceIcon.vue';
export { default as NetworkIcon } from './icons/NetworkIcon.vue';
export { default as CellularIcon } from './icons/CellularIcon.vue';
export { default as DeviceLoraIcon } from './icons/DeviceLoraIcon.vue';
export { default as DeviceManagementIcon } from './icons/DeviceManagementIcon.vue';
export { default as InfoIcon } from './icons/InfoIcon.vue';
export { default as DashboardIcon } from './icons/DashboardIcon.vue';
export { default as NotificationsIcon } from './icons/NotificationsIcon.vue';
export { default as GroupManagementIcon } from './icons/GroupManagementIcon.vue';
export { default as LocationManagementIcon } from './icons/LocationManagementIcon.vue';
export { default as ProfileIcon } from './icons/ProfileIcon.vue';
export { default as NotesIcon } from './icons/NotesIcon.vue';
export { default as ExitIcon } from './icons/ExitIcon.vue';
export { default as ProfileIconBordered } from './icons/ProfileIconBordered.vue';
export { default as MotionIcon } from './icons/MotionIcon.vue';
export { default as StatusBadIcon } from './icons/StatusBadIcon.vue';
export { default as StatusNeutralIcon } from './icons/StatusNeutralIcon.vue';
export { default as StatusWarningIcon } from './icons/StatusWarningIcon.vue';
export { default as StatusPollutedIcon } from './icons/StatusPollutedIcon.vue';
export { default as StatusExcellentIcon } from './icons/StatusExcellentIcon.vue';
export { default as ExitFullscreenIcon } from './icons/ExitFullscreenIcon.vue';
export { default as AlarmIcon } from './icons/AlarmIcon.vue';
export { default as AvatarIcon } from './icons/AvatarIcon.vue';
export { default as BankIdIcon } from './icons/BankIdIcon.vue';
export { default as DotsIcon } from './icons/DotsIcon.vue';
export { default as LockIcon } from './icons/LockIcon.vue';
export { default as MapPinIcon } from './icons/MapPinIcon.vue';
export { default as MarkerIcon } from './icons/MarkerIcon.vue';
export { default as CsvDocumentIcon } from './icons/CsvDocumentIcon.vue';
export { default as FilterIcon } from './icons/FilterIcon.vue';
export { default as StarIcon } from './icons/StarIcon.vue';
export { default as ReleaseNotesIcon } from './icons/ReleaseNotesIcon.vue';
export { default as TickIcon } from './icons/TickIcon.vue';
export { default as LoginIcon } from './icons/LoginIcon.vue';
export { default as LogoutIcon } from './icons/LogoutIcon.vue';
export { default as TempIcon } from './icons/TempIcon.vue';
export { default as LeftArrowIcon } from './icons/LeftArrowIcon.vue';
export { default as CollapseIcon } from './icons/CollapseIcon.vue';
export { default as ToggleNavOpen } from './components/icons/ToggleNavOpen.vue';
export { default as ToggleNavClose } from './components/icons/ToggleNavClose.vue';
export { default as UsersIcon } from './icons/UsersIcon.vue';
export { default as TargetIcon } from './icons/TargetIcon.vue';
export { default as AnalyzeIcon } from './icons/AnalyzeIcon.vue';

// Features
export { default as Maintenance } from './features/Maintenance';
export { ContextMenu, ContextSubMenu, ContextSubMenuItem, ContextMenuItem, ContextMenuSeparator } from './features/contextMenu';

// Templates
export const MapGoogle = defineAsyncComponent(async () => await import('./templates/MapGoogle.vue'));
export const Well = defineAsyncComponent(async () => await import('./templates/WellWidget.vue'));
export const Consumption = defineAsyncComponent(async () => await import('./templates/Consumption.vue'));
export { default as RelayToggleButton } from './components/relay-toggle-button/RelayToggleButton.vue';
export { default as DeviceMeta } from './templates/DeviceMeta';
export { default as Relay } from './templates/Relay';
export const IndoorClimate = defineAsyncComponent(async () => await import('./templates/IndoorClimate'));
export const WeatherWidget = defineAsyncComponent(async () => await import('./templates/Weather.vue'));
export const AirQualityWidget = defineAsyncComponent(async () => await import('./templates/AirQuality'));
export const TempWidget = defineAsyncComponent(async () => await import('./templates/TempWidget.vue'));
export { default as SkeletonUserTable } from './templates/SkeletonUserTable.vue';
export const UserManagementTable = defineAsyncComponent(async () => await import('./templates/UserMangagementTable.vue'));
export { default as PersonalInfo } from './templates/PersonalInfo.vue';
export { default as ChangePassword } from './templates/ChangePassword.vue';
export const AccountManagementTable = defineAsyncComponent(async () => await import('./templates/AccountManagementTable.vue'));
export { default as ManualLinkGroupSelect } from './templates/ManualLinkGroupSelect.vue';
export { default as Group } from './templates/Group.vue';
export { default as ActionBar } from './templates/Actionbar.vue';
export { default as UpdatableProperty } from './templates/UpdatableProperty.vue';
export const DateRangePicker = defineAsyncComponent(async () => await import('./templates/DatePicker.vue'));
export const GaugeChart = defineAsyncComponent(async () => await import('./templates/GaugeChart.vue'));
export { default as FileUpload } from './templates/FileUpload.vue';

// Composables

export { useColorModeObserver } from './composables/use-color-mode-observer';

// Transitions

export { default as ExpandTransition } from './transitions/ExpandTransition.vue';

// Theme tokens

export { THEME_TOKENS, type ThemeToken } from './theme-tokens';
